/**
 * src/engine/gainsTax.ts — the seam between the two capital-gains models (§6.12b).
 *
 * Every place that realizes a gain — the sell path, the forced-liquidation cure, the
 * estate step-up figure — asks a `GainsTaxModel` rather than multiplying by a constant.
 * That keeps the flat model byte-identical to what it always was (so its independently
 * verified regression anchors still prove the engine works) while letting the bracketed
 * model carry per-tax-year state.
 *
 * Models are STATEFUL by design: brackets depend on gain already realized this year, and
 * the IRS does not care whether that gain came from a routine sale or a margin call. Build
 * one per projection run, never share one across runs.
 */
import { capGainsBracketTableSchema } from '@/schema';
import type { CapGainsBracketTable, ScenarioInputs, TaxFields } from '@/types';
import bracketJson from '@/data/capGainsBrackets.json';
import {
  type BracketedSale,
  type ResolvedBrackets,
  grossUpBracketed,
  newTaxYearPosition,
  resolveBrackets,
  taxOnGain,
} from './capGainsBrackets';
import { combinedCapGainsRate } from './tax';

/**
 * The bundled bracket table, validated at module load (§7.3). Bundled rather than fetched:
 * it changes once a year, from an IRS Revenue Procedure PDF with no machine-readable feed,
 * so a scheduled job opens a merge request against this file rather than pushing to it.
 * `isBracketTableStale` is the gate that stops a stale copy shipping silently.
 */
export const CAP_GAINS_BRACKETS = capGainsBracketTableSchema.parse(
  bracketJson,
) as CapGainsBracketTable;

export interface GainsTaxModel {
  /**
   * Move the model to a calendar year. Bracket state resets on a new year; the flat model
   * ignores this entirely.
   */
  setYear(year: number): void;
  /** Tax owed on realizing `gain` right now, WITHOUT recording it. */
  taxOn(gain: number): number;
  /** Record `gain` as realized this year (advances the bracket position). */
  realize(gain: number): void;
  /**
   * A single representative rate (fraction) for callers that must solve in closed form —
   * the forced-liquidation cure. For the flat model this is exact. For the bracketed model
   * it is the marginal rate at the current position, which the caller refines.
   */
  rateHint(): number;
  /** Gross up to net `net` from an asset with gain fraction `g`, capped at `available`. */
  grossUpToNet(net: number, g: number, available: number): BracketedSale;
}

/** Today's model: one rate on every dollar of gain, forever. */
export class FlatGainsTax implements GainsTaxModel {
  constructor(private readonly rate: number) {}
  setYear(_year: number): void {}
  taxOn(gain: number): number {
    return Math.max(0, gain) * this.rate;
  }
  realize(_gain: number): void {}
  rateHint(): number {
    return this.rate;
  }
  grossUpToNet(net: number, g: number, available: number): BracketedSale {
    if (net <= 0 || available <= 0) return { grossSale: 0, gain: 0, tax: 0, denomInvalid: false };
    const netFactor = 1 - g * this.rate;
    if (netFactor <= 0) {
      // Gross-up unbounded: liquidate what is there and flag (§6.14).
      return { grossSale: available, gain: available * g, tax: available * g * this.rate, denomInvalid: true };
    }
    const grossSale = Math.min(net / netFactor, available);
    return { grossSale, gain: grossSale * g, tax: grossSale * g * this.rate, denomInvalid: false };
  }
}

/** Real 0/15/20 brackets plus NIIT, tracked per calendar year (§6.12b). */
export class BracketedGainsTax implements GainsTaxModel {
  private position = newTaxYearPosition(0);
  private brackets: ResolvedBrackets;

  constructor(
    private readonly tax: TaxFields,
    private readonly table: CapGainsBracketTable,
    /** Inflation as a fraction per year (0.025), applied to the federal thresholds. */
    private readonly inflationRate = 0,
    /** The projection's first calendar year — indexing is measured from it. */
    private readonly baseYear = 0,
  ) {
    this.brackets = resolveBrackets(tax, table);
  }

  setYear(year: number): void {
    if (year === this.position.year) return;
    this.position = newTaxYearPosition(year);
    // The IRS re-indexes the federal thresholds annually; NIIT stays nominal (§6.12b).
    const yearsElapsed = this.baseYear > 0 ? Math.max(0, year - this.baseYear) : 0;
    const factor = this.tax.indexBracketsToInflation
      ? Math.pow(1 + this.inflationRate, yearsElapsed)
      : 1;
    this.brackets = resolveBrackets(this.tax, this.table, factor);
  }

  taxOn(gain: number): number {
    return taxOnGain(gain, this.tax, this.brackets, this.position);
  }

  realize(gain: number): void {
    if (gain > 0) this.position.realizedGain += gain;
  }

  rateHint(): number {
    // Marginal rate on the next dollar — the cure refines this against the actual gain.
    return this.taxOn(1);
  }

  grossUpToNet(net: number, g: number, available: number): BracketedSale {
    return grossUpBracketed(net, g, available, this.tax, this.brackets, this.position);
  }

}

/**
 * Build the model a scenario asks for (§6.12b). Each call returns a FRESH model — the
 * borrow path's forced liquidations and the sell path's routine sales are alternative
 * worlds and must never share a tax-year position.
 */
export function buildGainsTaxModel(
  inputs: ScenarioInputs,
  baseYear = 0,
  table: CapGainsBracketTable = CAP_GAINS_BRACKETS,
): GainsTaxModel {
  if (!inputs.tax.useBracketedCapGains) {
    return new FlatGainsTax(combinedCapGainsRate(inputs.tax));
  }
  return new BracketedGainsTax(inputs.tax, table, inputs.inflationAnnualPct / 100, baseYear);
}
